/**
 * WHERE A CALL IS ALLOWED TO LIVE — the one decision, made once.
 *
 * A browser tab cannot put a call in its own window: the second window boots
 * with no access token, rotates the single-use refresh cookie it SHARES with
 * the opener, and signs you out of both. So in a browser the call stays in this
 * document and shrinks to a corner tile when you walk away from `/call/:id`.
 *
 * The desktop shell has none of that problem — the main process brokers the
 * token (`main/authBroker.ts`) and there is no pop-up blocker — and it has the
 * thing a browser cannot offer: a real window, with a taskbar entry, that
 * minimises and comes back. So on the desktop the call goes where Microsoft
 * Teams puts it, and the workspace behind it stays fully usable.
 *
 * THREE CASES, ONE PREDICATE:
 *   browser                → inline. Unchanged, forever.
 *   desktop workspace      → window. Hand the call to the shell and stay put.
 *   desktop CALL window    → inline, because this window IS the call.
 *
 * ...AND A FOURTH THAT IS NOT A CASE BUT A VERSION. The shell loads a REMOTE
 * workspace precisely so that the client and the server can be upgraded
 * independently, so a new web bundle inside an OLDER shell is an ordinary
 * Tuesday. Every verb below is capability-checked for that reason, and a shell
 * that cannot open call windows falls back to the inline surface every version
 * has always been able to render — see `callSurfaceMode`.
 *
 * Every entry point into a call funnels through `useOpenCall`, which asks here.
 */
import { desktopBridge } from './bridge';
import type {
  DesktopAuthPublish,
  DesktopBridge,
  DesktopCallActivity,
  DesktopCallWindowRequest,
} from './types';

export type CallSurfaceMode = 'inline' | 'window';

/**
 * Runs one bridge verb, tolerating both a MISSING verb and a failed IPC call.
 *
 * `withDesktop` in bridge.ts catches a synchronous throw and a rejection alike,
 * which is what made the version-skew bug silent: on an older shell
 * `bridge.openCall` was `undefined`, the TypeError landed in the same catch as
 * a dead IPC channel, and clicking Call did nothing at all with nothing logged
 * anywhere. Here a missing verb is a distinct, checkable outcome and every
 * caller gets a value it can fall back on.
 */
function callVerb<T>(
  pick: (bridge: DesktopBridge) => unknown,
  run: (bridge: DesktopBridge) => Promise<T>,
  fallback: T,
): Promise<T> {
  const bridge = desktopBridge();
  if (bridge === null || typeof pick(bridge) !== 'function') return Promise.resolve(fallback);
  try {
    return run(bridge).catch(() => fallback);
  } catch {
    /* The shell went away mid-call (reload, quit). Nothing to do. */
    return Promise.resolve(fallback);
  }
}

/** True only in a desktop window the shell opened for one specific call. */
export function isDesktopCallWindow(): boolean {
  return desktopBridge()?.isCallWindow === true;
}

/**
 * Can this shell actually put a call in a window?
 *
 * Both halves are needed. `isCallWindow` is how a document learns which kind it
 * is, and `openCall` is what the workspace does about it; a shell with neither
 * predates the feature, and the honest answer for it is the inline surface.
 */
function canOpenCallWindows(bridge: DesktopBridge): boolean {
  return typeof bridge.openCall === 'function' && typeof bridge.isCallWindow === 'boolean';
}

export function callSurfaceMode(): CallSurfaceMode {
  const bridge = desktopBridge();
  if (bridge === null) return 'inline';
  if (bridge.isCallWindow === true) return 'inline';
  return canOpenCallWindows(bridge) ? 'window' : 'inline';
}

/** Asks the shell for a window for this call. Focuses one that already exists. */
export function openDesktopCallWindow(request: DesktopCallWindowRequest): void {
  void callVerb(
    (bridge) => bridge.openCall,
    (bridge) => bridge.openCall?.(request) ?? Promise.resolve(false),
    false,
  );
}

export function focusDesktopCallWindow(callId: string): void {
  void callVerb(
    (bridge) => bridge.focusCall,
    (bridge) => bridge.focusCall?.(callId) ?? Promise.resolve(),
    undefined,
  );
}

/**
 * Closes THIS call window.
 *
 * The id travels for the shell's logs only: from a call window the shell closes
 * the window that ASKED, by identity, never by call id. It has to — this window
 * may be holding a different call by now ("Call again", the consult leg of an
 * attended transfer) — and a close addressed to the call it was opened with
 * matched nothing, so a finished call sat in the taskbar indefinitely.
 */
export function closeDesktopCallWindow(callId: string): void {
  void callVerb(
    (bridge) => bridge.closeCall,
    (bridge) => bridge.closeCall?.(callId) ?? Promise.resolve(),
    undefined,
  );
}

/**
 * "THIS WINDOW NOW HOLDS THAT CALL" — the re-keying half of the same problem.
 *
 * The shell keys a call window by its own identity and keeps the call as a
 * mutable property, so every arrival at a call inside a call window has to say
 * so. Resolves `false` when another live window already holds the call: the
 * caller must then FOCUS that window instead of navigating, which is the single
 * check standing between finishing an attended transfer with Merge and two
 * media sessions publishing into one SFU room.
 *
 * Resolves `true` outside a desktop call window, and on a shell too old to have
 * a registry — there is no bookkeeping to keep straight in either, and refusing
 * would break "Call again" for nothing.
 */
export function claimDesktopCallWindow(callId: string): Promise<boolean> {
  if (!isDesktopCallWindow()) return Promise.resolve(true);
  return callVerb(
    (bridge) => bridge.claimCall,
    (bridge) => bridge.claimCall?.(callId) ?? Promise.resolve(true),
    true,
  );
}

/**
 * Sends the WORKSPACE window somewhere, and leaves this one out of it.
 *
 * For the one destination a call window must be able to reach that is not a
 * call: the meeting a call was escalated into. Navigating the CALL window there
 * turned it into a full meeting room still keyed to a call that no longer
 * existed — its close button hung up nothing and it never closed itself.
 */
export function navigateDesktopWorkspace(path: string): void {
  void callVerb(
    (bridge) => bridge.navigateWorkspace,
    (bridge) => bridge.navigateWorkspace?.(path) ?? Promise.resolve(),
    undefined,
  );
}

/**
 * Whether a call window opened on a LIVE call or on a ringing one.
 *
 * The window takes the cold `POST /calls/:id/join` path, which reports
 * "already connected" by construction — it exists for rejoins. Without the
 * `ring=1` the shell puts on the URL for an outgoing call, placing a call on
 * the desktop would open a silent empty room instead of "Ringing…".
 *
 * Cosmetic, and therefore safe as a query parameter: the worst a forged one can
 * do is show a ringing overlay over a room that is already connected, which the
 * first `call.connected` event clears anyway.
 */
export function callWindowStartsConnected(): boolean {
  if (!isDesktopCallWindow() || typeof window === 'undefined') return true;
  return new URLSearchParams(window.location.search).get('ring') !== '1';
}

/* ── Presence across two windows, renderer side ──────────────────────────── */

/**
 * THIS CALL WINDOW, REPORTING WHAT IT IS DOING TO THE ONE DOCUMENT THAT
 * HEARTBEATS.
 *
 * A person has one presence, and `PresenceRuntime` — the idle monitor and the
 * heartbeat — is deliberately mounted only in the workspace, because two of
 * either for one human disagree about whether they are here. So the call
 * window, which is the document that actually knows there is a call, tells the
 * shell; the shell aggregates across every call window; the workspace folds the
 * answer in. Without this the workspace — which sees no input while the user
 * talks in here — aged to Away five minutes into every call and repainted the
 * caller's OWN chip and OWN tray icon with it. LOCAL ONLY, and worth stating
 * because an earlier version of this comment claimed the roster never saw "In a
 * call" at all: it always did. `activity_state` is derived by trigger from
 * `call_participants` (infra/sql/062, "Never client-set") and pinned into tier 1
 * as `mo`, and the client heartbeat that would have carried anything else is an
 * empty function with no route behind it.
 *
 * A no-op anywhere but a call window: nowhere else has anything to report, and
 * the shell refuses a report from anywhere else regardless.
 */
export function publishCallActivity(activity: DesktopCallActivity): void {
  if (!isDesktopCallWindow()) return;
  void callVerb(
    (bridge) => bridge.publishCallActivity,
    (bridge) => bridge.publishCallActivity?.(activity) ?? Promise.resolve(),
    undefined,
  );
}

/**
 * The WORKSPACE asking what the call windows are doing.
 *
 * Pull as well as push, exactly like the token broker and for the same reason:
 * a workspace that reloads mid-call comes back as a fresh document that missed
 * every push, and would otherwise report Available for the rest of the call.
 *
 * Resolves `null` when there is nothing to ask — a browser, a call window, or a
 * shell too old to know the question — and the caller leaves presence alone.
 */
export function readCallActivity(): Promise<DesktopCallActivity | null> {
  if (isDesktopCallWindow()) return Promise.resolve(null);
  return callVerb(
    (bridge) => bridge.callActivity,
    (bridge) => bridge.callActivity?.() ?? Promise.resolve(null),
    null,
  );
}

/* ── The access-token broker, renderer side ──────────────────────────────── */

function publish(state: DesktopAuthPublish): void {
  const bridge = desktopBridge();
  if (bridge === null || bridge.isCallWindow === true) return;
  void callVerb(
    (shell) => shell.publishAuthToken,
    (shell) => shell.publishAuthToken?.(state) ?? Promise.resolve(),
    undefined,
  );
}

/**
 * The workspace window reporting a TRANSITION in its token.
 *
 * Only this may say "signed out", because only a transition can mean it: the
 * token store notifies its subscribers when the value CHANGES, and it cannot
 * change to null from null. Reading null is a different thing entirely — see
 * `publishShellTokenState`.
 *
 * A no-op in a browser and in a call window. Only the workspace rotates, so
 * only the workspace has anything to publish, and the main process rejects a
 * publish from anywhere else regardless.
 */
export function publishShellToken(token: string | null): void {
  publish(token === null ? { kind: 'signed-out' } : { kind: 'token', token });
}

/**
 * The workspace window reporting what it currently HOLDS.
 *
 * A read, not a transition, so `null` here means "nothing yet" and never "the
 * session is over". This is the fix for a mid-call sign-out that a Ctrl+R in
 * the workspace was enough to cause: the access token is memory-only, every
 * fresh workspace document starts with none, and this runs before
 * `AuthProvider` has even issued its cold-start rotation.
 *
 * It is also the honest answer after a rotation that failed on the NETWORK
 * rather than at the API. `rotate()` deliberately keeps the token it holds in
 * that case ("dropping the session on a flaky network signs people out for
 * walking into a lift"), so publishing its `null` return as a sign-out would
 * have done to the call window exactly what it refuses to do to this one.
 */
export function publishShellTokenState(token: string | null): void {
  publish(token === null ? { kind: 'unknown' } : { kind: 'token', token });
}

/**
 * A call window asking the shell for a token INSTEAD of rotating.
 *
 * `force` says the token this window holds is no good — expired, revoked, or
 * never there at all — so the broker must make the workspace window rotate
 * rather than replay its cache.
 *
 * Resolves to `null` outside a call window, which is the signal to
 * `lib/api/refresh.ts` that this document is on the ordinary refresh path.
 */
export function requestShellToken(force: boolean): Promise<string | null> {
  if (!isDesktopCallWindow()) return Promise.resolve(null);
  return callVerb(
    (bridge) => bridge.requestAuthToken,
    (bridge) => bridge.requestAuthToken?.(force) ?? Promise.resolve(null),
    null,
  );
}
